import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

export default class APIStocks extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get a stock by ID
  get = async (stockId: UUID): Promise<types.StockResponse> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Alias for backward compatibility
  getStockId = this.get;

  // Get paginated stocks
  list = async (params?: {
    product_id?: UUID;
    partner_id?: UUID;
    lot_id?: UUID;
    location_id?: UUID;
    page?: number;
    size?: number;
  }): Promise<types.PageStockResponse> => {
    let res = await this._fetch({
      url: `/stocks/`,
      method: "GET",
      params: params as Record<string, unknown>,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get stocks with flow summary (aggregated inbound/outbound)
  getWithFlowSummary = async (): Promise<
    types.StockWithFlowSummaryResponse[]
  > => {
    let res = await this._fetch({
      url: `/stocks/with-flow-summary`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get provisional stock by ID (stock with provisional flows applied)
  getProvisional = async (
    stockId: UUID,
  ): Promise<types.ProvisionalStockResponse> => {
    let res = await this._fetch({
      url: `/stocks/provisional/${stockId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get provisional stocks by product ID
  getProvisionalByProduct = async (
    productId: UUID,
  ): Promise<types.ProvisionalStockResponse[]> => {
    let res = await this._fetch({
      url: `/stocks/provisional/product/${productId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create a new stock
  create = async (
    stockData: types.StockCreate,
  ): Promise<types.StockResponse> => {
    let res = await this._fetch({
      url: `/stocks/`,
      method: "POST",
      body: stockData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update a stock
  update = async (
    stockId: UUID,
    stockData: types.StockUpdate,
  ): Promise<types.StockResponse> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}`,
      method: "PUT",
      body: stockData,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Alias for backward compatibility
  putStockId = this.update;

  // Delete a stock
  delete = async (stockId: UUID): Promise<Response> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    return res;
  };

  // Alias for backward compatibility
  deleteStockId = this.delete;

  // Get temporary stocks (legacy feature - returns empty array)
  // TODO: This endpoint doesn't exist in the backend. Either implement it or remove the feature.
  getTemporaryStocks = async (): Promise<
    Array<{
      product_id: string;
      weight_amount: number;
      unit_amount: number | null;
      price_per_weight: number;
    }>
  > => {
    // Return empty array as this endpoint is not implemented
    return [];
  };

  // Lightweight stock summary for recap dropdown with delta fetch support
  getRecapSummary = async (
    since?: string,
  ): Promise<types.StockRecapSummaryResponse> => {
    let res = await this._fetch({
      url: `/stocks/recap-summary`,
      method: "GET",
      params: since ? { since } : undefined,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get stock validation data for the resserre page
  getValidation = async (): Promise<types.StockValidationEntry[]> => {
    let res = await this._fetch({
      url: `/stocks/validation`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // ============================================================================
  // Aggregation, Historical, and Evolution Methods
  // ============================================================================

  // Get stock aggregations grouped by product tags
  getAggregatedByTag = async (params?: {
    tag?: string;
  }): Promise<types.StockAggregationByTagResponse> => {
    let res = await this._fetch({
      url: `/stocks/aggregated-by-tag`,
      method: "GET",
      params: params as Record<string, unknown>,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get stock quantities at a specific historical date
  getAtDate = async (
    request: types.StockAtDateRequest,
  ): Promise<types.StockAtDateResponse> => {
    let res = await this._fetch({
      url: `/stocks/at-date`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get stock evolution time-series data
  getEvolution = async (
    request: types.StockEvolutionRequest,
  ): Promise<types.StockEvolutionResponse> => {
    let res = await this._fetch({
      url: `/stocks/evolution`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get stock projection with daily flow details for chart visualization
  getProjection = async (
    stockId: UUID,
    request: types.StockProjectionRequest,
  ): Promise<types.StockProjectionResponse> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/projection`,
      method: "POST",
      body: request,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get complete movement history for a stock (flows + transfers)
  getMovements = async (stockId: UUID): Promise<types.StockMovementItem[]> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/movements`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get point-in-time snapshots for a stock
  getHistory = async (
    stockId: UUID,
    limit?: number,
  ): Promise<types.StockHistoryResponse[]> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/history`,
      method: "GET",
      params: limit ? { limit } : undefined,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Backfill historical snapshots for a single stock
  backfillHistory = async (
    stockId: UUID,
  ): Promise<types.StockHistoryResponse[]> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/history/backfill`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Backfill historical snapshots for all stocks
  backfillAllHistory = async (): Promise<{
    message: string;
    stocks_processed: number;
    snapshots_created: number;
  }> => {
    let res = await this._fetch({
      url: `/stocks/history/backfill-all`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Document management methods

  // Get documents for a stock
  getDocuments = async (
    stockId: UUID,
  ): Promise<{ stock_id: UUID; files: types.StockFile[] }> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/documents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Upload documents to a stock
  uploadDocuments = async (
    stockId: UUID,
    files: File[],
  ): Promise<{ message: string; files: types.StockFile[]; stock_id: UUID }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    let res = await this._fetch({
      url: `/stocks/${stockId}/documents/upload`,
      method: "POST",
      body: formData,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Remove a document from a stock
  removeDocument = async (
    stockId: UUID,
    fileUrl: string,
  ): Promise<{
    message: string;
    removed_file: types.StockFile;
    stock_id: UUID;
  }> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/documents/remove`,
      method: "DELETE",
      params: { file_url: fileUrl },
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Download a document from a stock
  downloadDocument = async (stockId: UUID, fileUrl: string): Promise<Blob> => {
    let res = await this._fetch({
      url: `/stocks/${stockId}/documents/download`,
      method: "GET",
      params: { file_url: fileUrl },
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };
}
